import { Box, FormHelperText, FormLabel, SxProps } from '@mui/material'
import { Dropzone, UploadedFile } from '@sitoo/mui-components'
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Maybe, Resource } from '../../generated/graphql'
import { useEffect, useState } from 'react'
import { DropzoneProps } from '@sitoo/mui-components/dist/types/src/components/dropzone'

export type Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TMultiple extends boolean,
  TValue = TMultiple extends true ? UploadedFile[] : UploadedFile,
> = UseControllerProps<TFieldValues, TName> & {
  isLoading?: boolean
  resource?: Maybe<Resource>
  label?: string
  helperText?: string
  maxSize?: number
  multiple?: TMultiple
  sx?: SxProps
  hideFileName?: boolean
  accept?: DropzoneProps['accept']
  onChange?: (files: TValue | null) => void
  backgroundColor?: string
}

export const DropzoneInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TMultiple extends boolean = true,
  TValue = TMultiple extends true ? UploadedFile[] : UploadedFile,
>(
  props: Props<TFieldValues, TName, TMultiple, TValue>,
) => {
  const { t } = useTranslation(['pos_profiles'])

  const [isReady, setReady] = useState(false)

  const {
    name,
    control,
    label,
    resource,
    isLoading,
    onChange,
    ...dropzoneProps
  } = props

  const { fieldState, field } = useController({ name, control })

  const errorMessage = fieldState.error?.message

  const preloadedFiles = resource
    ? [
        {
          fileUrl: resource?.url ?? undefined,
          id: resource?.resourceid ?? undefined,
          file: {
            name: resource?.resourceid,
            size: resource?.filesize,
          } as File,
        },
      ]
    : []

  const updateLogotype = (files: UploadedFile[]) => {
    const value = (props.multiple ? files : files[0]) as TValue

    field.onChange(value ?? null)
    onChange?.(value ?? null)
  }

  useEffect(() => {
    if (!isLoading) setReady(true)
  }, [isLoading])

  return (
    <Box>
      <FormLabel sx={{ display: 'block', mb: 2 }}>{label}</FormLabel>

      {/*
       * NOTE: Currently, the only way to update preloadFiles is by remounting
       * the Dropzone. Ideally, Dropzone should support controlled updates.
       * Consider renaming preloadFiles to defaultValue, and adding a value
       * prop to allow dynamic updates.
       */}
      {!isReady ? (
        <Box sx={{ width: '100%', height: '130px' }} />
      ) : (
        <Dropzone
          preloadFiles={preloadedFiles}
          setFiles={updateLogotype}
          downloadUrl={resource?.downloadUrl}
          sx={{
            width: '100%',
            '.DropzoneContainer': { height: '130px' },
          }}
          optionsMenu
          optionsMenuLabels={{
            delete: t('shared:action.delete'),
            download: t('shared:action.download'),
            replace: t('shared:action.replace'),
          }}
          {...dropzoneProps}
        />
      )}

      <FormHelperText error>{errorMessage}</FormHelperText>
    </Box>
  )
}
